import { useEffect, useState } from "react";
import { Dropdown, Table } from "react-bootstrap";
import styled from "styled-components";

import { PayFacilityUsage } from "../types/PayFacilityData";
import { BulkInput, PayFacilityMeta } from "../types/PayFacilityMeta";
import { NameID } from "common/types/NameID";

import { RowEditButtons } from "common/components/molecules/RowEditButtons";
import { MultiLineText } from "common/components/atoms/MultiLineText";
import FunctionButton from "common/components/atoms/FunctionButton";
import MyNumberInput from "common/components/atoms/MyNumberInput";

import { getHolidayName } from "utils/commonTools";

import { usePayFacilityData } from "../hooks/usePayFacilityData";
import { DAY_TYPE } from "common/types/consts/Defines";

type Props = {
  Usages: Array<PayFacilityUsage> | undefined;
  Meta: PayFacilityMeta;
  date: Date;
  paymentMethods: NameID[] | undefined;
  readonly: boolean;
  addDataRequested: () => void;
  dataEdited: () => void;
  rangeDataAdded: (data: PayFacilityUsage[]) => void;
  editDataRequested: (data: PayFacilityUsage, index: number) => void;
  deleteDataRequested: (data: PayFacilityUsage, index: number) => void;
};

export const PayFacilityDetailTable = ({
  Usages,
  Meta,
  date,
  paymentMethods,
  readonly,
  addDataRequested,
  dataEdited,
  rangeDataAdded,
  editDataRequested,
  deleteDataRequested,
}: Props) => {
  const [data, setData] = useState<PayFacilityUsage[]>();
  const { createEmptyPayFacilityUsage, calcFee: calcFeeExec } = usePayFacilityData();
  const addRange = (input: BulkInput) => {
    const dataAdded = input.inputValues.map((x) => {
      const newData = createEmptyPayFacilityUsage();
      newData.usageType = x.usage ?? null;
      newData.periodType = x.period ?? null;
      newData.exemptionType = x.exemption ?? null;
      newData.paymentType = x.paymentMethod ?? null;
      return newData;
    });
    rangeDataAdded(dataAdded);
    setData([...(data ?? []), ...dataAdded]);
  };

  const calcFee = (item: PayFacilityUsage) => {
    const dayType = getHolidayName(date) ? DAY_TYPE.HOLIDAY : DAY_TYPE.NORMAL;
    return calcFeeExec(item, Meta.fees, dayType);
  };

  useEffect(() => {
    setData(Usages);
  }, [Usages]);

  return (
    <>
      <SButtonDiv>
        {/* 一括追加 */}
        {!readonly && (Meta.bulkInputs?.length ?? 0) > 0 && (
          <Dropdown drop="down-centered">
            <SDropdownToggle>一括追加</SDropdownToggle>
            <Dropdown.Menu>
              {Meta.bulkInputs?.map((x, index) => (
                <Dropdown.Item
                  key={`menuAddRange_${x.bulkName}_${index}`}
                  onClick={(e) => addRange(x)}
                >
                  {x.bulkName}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        )}

        {/* 追加ボタン */}
        {!readonly && (
          <FunctionButton
            onClick={(e) => {
              e.preventDefault();
              addDataRequested();
            }}
            className="ms-2"
            tooltip="追加"
          >
            <i className="fa-regular fa-plus"></i>
          </FunctionButton>
        )}
      </SButtonDiv>
      <Table bordered hover responsive>
        <thead>
          <tr className="text-center">
            {Meta.usageTypes.length > 0 && <Sth1 className="col-md-1">利用区分</Sth1>}
            {Meta.periodTypes.length > 0 && <Sth1 className="col-md-1">時間区分</Sth1>}
            {Meta.exemptionTypes.length > 0 && <Sth1 className="col-md-1">減免適用</Sth1>}
            {(paymentMethods?.length ?? 0) > 0 && <Sth1 className="col-md-1">支払方法</Sth1>}
            <Sth1 className="col-md-1">利用件数</Sth1>
            <Sth1 className="col-md-1">利用人数</Sth1>
            <th className="col-md-1" style={{ minWidth: "110px" }}>
              利用料金
            </th>
            <th className="col-md-*" style={{ minWidth: "100px" }}>
              備考
            </th>
            {!readonly && <Sth1 className="editButtonCol">操作</Sth1>}
          </tr>
        </thead>
        <tbody>
          {data &&
            data.map((x, index) => (
              <tr key={`payfacuse_${index}`} style={{ verticalAlign: "middle" }}>
                {Meta.usageTypes.length > 0 && <td className="text-center">{x.usageType?.name}</td>}
                {Meta.periodTypes.length > 0 && (
                  <td className="text-center"> {x.periodType?.name} </td>
                )}
                {Meta.exemptionTypes.length > 0 && (
                  <td className="text-center">{x.exemptionType?.name}</td>
                )}
                {(paymentMethods?.length ?? 0) > 0 && (
                  <td className="text-center">{x.paymentType?.name}</td>
                )}
                <td className="text-end">
                  <SMyNumberInput
                    initialValue={x.count}
                    changed={(value) => {
                      x.count = value;
                      x.fee = calcFee(x);
                      dataEdited();
                    }}
                    readonly={readonly}
                  />
                </td>
                <td className="text-end">
                  <SMyNumberInput
                    initialValue={x.numUser}
                    changed={(value) => {
                      x.numUser = value;
                      dataEdited();
                    }}
                    readonly={readonly}
                  />
                </td>
                <td className="text-end">
                  <SFeeCol>
                    &yen;
                    {readonly ? (
                      <span>{x.fee.toLocaleString()}</span>
                    ) : (
                      <SMyNumberInput
                        initialValue={x.fee}
                        changed={(value) => {
                          x.fee = value;
                          dataEdited();
                        }}
                        readonly={readonly}
                        step={100}
                      />
                    )}
                  </SFeeCol>
                </td>
                <td className="text-start">
                  <MultiLineText text={x.note} />{" "}
                </td>
                {!readonly && (
                  <StdButtons className="text-center">
                    <RowEditButtons
                      editRequested={() => editDataRequested(x, index)}
                      deleteRequested={() => deleteDataRequested(x, index)}
                    />
                  </StdButtons>
                )}
              </tr>
            ))}
        </tbody>
      </Table>
    </>
  );
};

const SButtonDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
`;

const SDropdownToggle = styled(Dropdown.Toggle)`
  background: ${(props) => props.theme.color.functionButton};
  background-image: none;
  &.btn.show,
  &.btn-primary {
    :hover {
      background: ${(props) => props.theme.color.functionButtonHover};
      background-image: none;
    }
    :not(:hover) {
      background: ${(props) => props.theme.color.functionButton};
      background-image: none;
    }
  }
`;

const Sth1 = styled.th`
  min-width: 80px;
`;

const SMyNumberInput = styled(MyNumberInput)`
  font-size: 14px;
`;

const SFeeCol = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 5px;
`;

const StdButtons = styled.td`
  display: flex;
  flex-direction: row;
  button {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
`;
